import { useEffect, useState } from "react";

import { keepPreviousData, useQuery } from "@tanstack/react-query";
import ApiHttp from "byzantine/src/ApiHttp";

export const TRANSACTION_SEARCH_QUERY_KEY = "receiptTransactionSearch";
export const TRANSACTION_SEARCH_DEBOUNCE_MS = 300;
const RESULTS_PER_PAGE = 8;

export interface UseTransactionSearchResult {
  results: API.Transaction[];
  isSearching: boolean;
  isError: boolean;
}

interface TransactionsListBody {
  transactions: API.Transaction[];
}

const isNotFound = (error: unknown): boolean =>
  typeof error === "object" &&
  error !== null &&
  (error as { response?: { status?: number } }).response?.status === 404;

// The raw input goes through as `q` untouched. The endpoint's search DSL
// matches bare terms against text columns and the amount column, so merchant
// text and "87.42" both work with zero client-side parsing.
export const useTransactionSearch = (
  input: string
): UseTransactionSearchResult => {
  const [debouncedInput, setDebouncedInput] = useState("");

  useEffect(() => {
    if (input.trim() === "") {
      setDebouncedInput("");
      return undefined;
    }
    const timer = setTimeout(
      () => setDebouncedInput(input),
      TRANSACTION_SEARCH_DEBOUNCE_MS
    );
    return () => clearTimeout(timer);
  }, [input]);

  const enabled = debouncedInput.trim() !== "";

  const query = useQuery<API.Transaction[]>({
    queryKey: [TRANSACTION_SEARCH_QUERY_KEY, debouncedInput],
    queryFn: async () => {
      try {
        // rawResponse keeps the 404 status; ApiHttp otherwise flattens it to
        // a permissions string. This endpoint 404s on empty result sets.
        const response = await ApiHttp.fetch<{ data: TransactionsListBody }>(
          "transactions",
          { rawResponse: true },
          {
            q: debouncedInput,
            per_page: RESULTS_PER_PAGE,
            include_receipts: "true",
          }
        );
        // Pending rows arrive merged in; only settled transactions can hold
        // a receipt.
        return response.data.transactions.filter((transaction) =>
          Boolean(transaction.settled_at)
        );
      } catch (error) {
        if (isNotFound(error)) return [];
        throw error;
      }
    },
    enabled,
    staleTime: 0,
    gcTime: 0,
    retry: false,
    placeholderData: keepPreviousData,
  });

  const isDebouncing = input.trim() !== "" && debouncedInput !== input;

  // keepPreviousData only smooths pagination-style refetches; rows from a
  // previous term must not be selectable under the new term, so stale
  // placeholder data is hidden and the dropdown shows its loading row.
  const isStale = isDebouncing || query.isPlaceholderData;

  return {
    results: enabled && !isStale ? query.data ?? [] : [],
    isSearching: isDebouncing || (enabled && query.isFetching),
    isError: enabled && query.isError,
  };
};
